"use client"

import { createContext, useContext, useId, useState } from "react"
import type { ReactNode } from "react"

type TabsContextValue = {
  value: string
  setValue: (v: string) => void
  scope: string
}

const TabsContext = createContext<TabsContextValue | null>(null)

function useTabsContext() {
  const ctx = useContext(TabsContext)
  if (!ctx) {
    throw new Error("Tabs.List / .Trigger / .Content must be used inside <Tabs>")
  }
  return ctx
}

export type TabsProps = {
  value?: string
  defaultValue?: string
  onChange?: (v: string) => void
  children: ReactNode
  className?: string
}

export function Tabs({
  value: controlled,
  defaultValue,
  onChange,
  children,
  className = "",
}: TabsProps) {
  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? "")
  const scope = useId()
  const isControlled = controlled !== undefined
  const value = isControlled ? controlled : uncontrolled

  const setValue = (v: string) => {
    if (!isControlled) setUncontrolled(v)
    onChange?.(v)
  }

  return (
    <TabsContext.Provider value={{ value, setValue, scope }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  )
}

function TabsList({
  children,
  className = "",
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      role="tablist"
      className={`border-b border-[var(--redy-border)] flex gap-1 overflow-x-auto no-scrollbar ${className}`}
    >
      {children}
    </div>
  )
}

function TabsTrigger({
  value,
  children,
  className = "",
}: {
  value: string
  children: ReactNode
  className?: string
}) {
  const ctx = useTabsContext()
  const active = ctx.value === value
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-controls={`${ctx.scope}-panel-${value}`}
      id={`${ctx.scope}-tab-${value}`}
      onClick={() => ctx.setValue(value)}
      className={[
        "px-4 py-2.5 text-[13px] font-semibold border-b-2 transition-colors whitespace-nowrap -mb-px",
        active
          ? "text-[var(--redy-red)] border-[var(--redy-red)]"
          : "text-[var(--redy-muted)] border-transparent hover:text-[var(--redy-charcoal)]",
        className,
      ].join(" ")}
    >
      {children}
    </button>
  )
}

function TabsContent({
  value,
  children,
  className = "",
}: {
  value: string
  children: ReactNode
  className?: string
}) {
  const ctx = useTabsContext()
  if (ctx.value !== value) return null
  return (
    <div
      role="tabpanel"
      id={`${ctx.scope}-panel-${value}`}
      aria-labelledby={`${ctx.scope}-tab-${value}`}
      className={`pt-5 ${className}`}
    >
      {children}
    </div>
  )
}

Tabs.List = TabsList
Tabs.Trigger = TabsTrigger
Tabs.Content = TabsContent
